'use client';
import { useThrottleFn } from 'ahooks';
import { useCallback, useState } from 'react';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { creditsGetCostByApp } from '@/app/services/credit';
import type { ViduV1Container } from '@/app/services/vidu';
import {
  agentChat,
  agentCreateSession,
  agentDeleteSession,
  agentGetChatHistory,
  agentListModels,
  agentListVoices,
  agentRegenerateTts,
  canvasesCreate,
  canvasesGet,
  containersAction,
  containersList,
  containersMergeClips,
  containersOpenStoryboardContainers,
  containersRandomMusic,
  containersUpdate,
  projectsCreate,
  projectsGet,
  projectsGetMy,
  projectsListShowCase,
  projectsUpdate,
  projectsUseShowCase,
} from '@/app/services/vidu';

import { type AgentSettings, COST_TYPE, type CreditCostInfo } from '@/app/types';
import { getCostMapKey } from '@/app/utils';
import type { ContainersActionType } from './_types';
import { COST_FETCH_INTERVAL } from './constants';

export {
  containersMergeClips,
  agentListVoices,
  agentListModels,
  agentDeleteSession,
  agentGetChatHistory,
  agentChat,
  agentRegenerateTts,
};
export { projectsUseShowCase, projectsListShowCase };

/**
 * 获取项目列表
 * @returns Promise 包含项目列表数据
 */
export const useGetProjectList = () => {
  const {
    data,
    loading,
    run: fetchProjectList,
    runAsync: fetchProjectListAsync,
  } = useFetch(
    (page: number, pagesz?: number) => projectsGetMy({ query: { 'pager.page': page, 'pager.pagesz': pagesz || 30 } }),
    {
      manual: true,
    },
  );
  return {
    data: data?.data?.projects || [],
    total: data?.data?.total || 0,
    loading,
    fetchProjectList,
    fetchProjectListAsync,
  };
};

// 获取项目详情
export const useGetProjectDetail = () => {
  const {
    data,
    loading,
    run: fetchProjectDetail,
    runAsync: fetchProjectDetailAsync,
  } = useFetch(
    (projectId: string) =>
      projectsGet({
        path: {
          id: projectId,
        },
      }),
    {
      manual: true,
    },
  );
  return { data: data?.data, loading, fetchProjectDetail, fetchProjectDetailAsync };
};

// 修改项目
export const useUpdateProject = () => {
  const {
    data,
    loading,
    run: updateProject,
    runAsync: updateProjectAsync,
  } = useFetch(
    (projectId: string, mask: string, data?: { name?: string }) => {
      return projectsUpdate({
        path: {
          'project.id': projectId,
        },
        query: {
          id: projectId,
          mask: mask,
        },
        body: {
          id: projectId,
          ...data,
        },
      });
    },
    {
      manual: true,
    },
  );
  return { data: data?.data, loading, updateProject, updateProjectAsync };
};

export const useCreateProject = () => {
  const {
    data,
    loading,
    run: createProject,
    runAsync: createProjectAsync,
  } = useFetch(
    (data?: { name?: string }) => {
      return projectsCreate({
        body: {
          name: data?.name || `${Date.now()}`,
        },
      });
    },
    {
      manual: true,
    },
  );
  return { data: data?.data, loading, createProject, createProjectAsync };
};

// 创建画布
export const useCreateCanvas = () => {
  const {
    data,
    loading,
    run: createCanvas,
    runAsync: createCanvasAsync,
  } = useFetch(
    ({ project_id, name }: { project_id: string; name?: string }) => {
      return canvasesCreate({
        body: {
          project_id: project_id,
          name: name || `${Date.now()}`,
        },
      });
    },
    {
      manual: true,
    },
  );
  return { data: data?.data, loading, createCanvas, createCanvasAsync };
};

// 获取画布详情
export const useGetCanvasDetail = () => {
  const {
    data,
    loading,
    run: fetchCanvasDetail,
    runAsync: fetchCanvasDetailAsync,
  } = useFetch(
    (canvasId: string) =>
      canvasesGet({
        path: {
          id: canvasId,
        },
      }),
    {
      manual: true,
    },
  );
  return {
    data: data?.data,
    loading,
    fetchCanvasDetail,
    fetchCanvasDetailAsync,
  };
};

// 获取全部容器节点list（并发请求）
export const useGetContainerAllList = () => {
  // 传值 canvas_id，总数 和 从第几页开始
  const fetchContainerAllList = async (canvas_id: string, total: number, startPage = 0) => {
    const totalPages = Math.ceil(total / 100) - startPage;
    // 直接调用 containersList，每个请求都是独立的 Promise
    // 从第 startPage 页开始，到第 totalPages 页
    const promises = Array.from({ length: totalPages }, (_, page) =>
      containersList({
        query: {
          canvas_ids: [canvas_id],
          ids: [],
          'pager.page': page + startPage,
          'pager.pagesz': 100,
        },
      }).catch(() => null),
    );

    // 并发执行所有请求
    const results = await Promise.all(promises);

    // 合并所有成功的结果
    const allContainers = results.flatMap((result) => {
      return result?.data?.containers || [];
    });
    return allContainers;
  };

  return {
    fetchContainerAllList,
  };
};

// 根据条件获取容器节点list（单次请求）
export const useGetContainerList = () => {
  const { loading, runAsync } = useFetch(
    ({ canvas_id, ids = [], page = 0 }: { canvas_id: string; ids?: string[]; page?: number }) =>
      containersList({
        query: {
          canvas_ids: [canvas_id],
          ids: ids,
          'pager.page': page,
          'pager.pagesz': 100,
        },
      }),
    {
      manual: true,
    },
  );

  // 根据id获取容器节点list
  const fetchContainerListByIds = async (canvas_id: string, ids: string[]) => {
    const results = await runAsync({ canvas_id, ids });
    return {
      containers: results?.data?.containers || [],
      canvas_id: canvas_id,
    };
  };

  return {
    loading,
    fetchContainerListByIds,
  };
};

// 创建会话
export const useCreateChat = () => {
  const {
    data,
    loading,
    run: createChat,
    runAsync: createChatAsync,
  } = useFetch(
    (canvas_id: string) =>
      agentCreateSession({
        body: {
          canvas_id: canvas_id,
        },
      }),
    {
      manual: true,
    },
  );
  return { data, loading, createChat, createChatAsync };
};

// 修改容器内容
export const useUpdateContainerContent = () => {
  const {
    data,
    loading,
    run: updateContainerContent,
    runAsync: updateContainerContentAsync,
  } = useFetch(
    (container_id: string, mask: string, container: ViduV1Container) =>
      containersUpdate({
        path: {
          id: container_id,
        },
        body: {
          id: container_id,
          container: container,
          mask: mask,
        },
      }),
    {
      manual: true,
    },
  );
  return { data, loading, updateContainerContent, updateContainerContentAsync };
};

// 任务containersAction
export const useContainersAction = () => {
  const {
    data,
    loading,
    run: containersActionRequest,
    runAsync: containersActionAsync,
  } = useFetch(
    (
      container_id: string,
      action: ContainersActionType,
      settings?: AgentSettings,
      input_text?: string,
      reference_image_id?: string[],
    ) =>
      containersAction({
        path: {
          id: container_id,
        },
        body: {
          id: container_id,
          action: action,
          settings: {
            ratio: settings?.ratio,
          },
          input_text: input_text,
          container: {
            content: {
              video: {
                reference_image_id: reference_image_id,
              },
            },
          },
        },
      }),
    {
      manual: true,
    },
  );
  return { data, loading, containersActionRequest, containersActionAsync };
};

/**
 * 获取发送消息后的最新历史记录
 *
 * 用于在消息发送成功后获取服务器返回的完整消息（包括 AI 回复）
 *
 * @param sessionId - 会话 ID
 * @returns 返回消息数据和对应的 sessionId（用于验证）
 */
export async function getHistoryAfterSend(sessionId: string) {
  if (!sessionId) {
    return { data: { messages: [] }, chatId: sessionId };
  }

  const result = await agentGetChatHistory({
    query: {
      'pager.pagesz': 5,
      'pager.direction': 'backward',
    },
    path: {
      session_id: sessionId,
    },
  });

  return {
    ...result,
    chatId: sessionId, // 返回传入的 sessionId，用于验证
  };
}

/**
 * 获取随机音频
 * @returns 返回音频数据
 */
export const useGetRandomAudio = () => {
  const { loading: randomAudioLoading, runAsync: getRandomAudioAsync } = useFetch(
    () => containersRandomMusic({ body: {} }),
    {
      manual: true,
    },
  );
  return { randomAudioLoading, getRandomAudioAsync };
};

/**
 * 获取视频组下所有分镜节点
 */
export const useGetVideoGroupClips = () => {
  const { loading: videoGroupClipsLoading, runAsync: getVideoGroupClipsAsync } = useFetch(
    (canvas_id: string, videoGroup_id: string) =>
      containersOpenStoryboardContainers({ path: { canvas_id: canvas_id, video_group_id: videoGroup_id } }),
    {
      manual: true,
    },
  );
  return { videoGroupClipsLoading, getVideoGroupClipsAsync };
};

/**
 * 获取消耗积分的 hooks
 * 返回一个 Map，key 为 `${costType}_${duration}`，value 为积分消耗信息
 *
 * @example
 * const { costMap, loading, error, refetch } = useGetCreditCost();
 * const videoGenerate15Cost = costMap.get('video_generate_15');
 * const clipGenerate30Cost = costMap.get('clip_generate_30');
 */
export const useGetCreditCost = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const { setAgentCreditCostMap } = useStore((state) => ({
    setAgentCreditCostMap: state.setAgentCreditCostMap,
  }));

  const fetchCosts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const newCostMap = new Map<string, CreditCostInfo>();

      // 创建所有可能的组合请求
      const requests = Object.values(COST_TYPE).map((costType) =>
        [15, 30].map(async (duration) => {
          try {
            let result = null;
            if (costType === COST_TYPE.ZERO_CREDITS_VIDEO_GENERATE) {
              result = {
                data: {
                  cost_credits: 0,
                  original_credits: 0,
                },
              };
            } else {
              result = await creditsGetCostByApp({
                query: {
                  app: 'agent',
                  'agent_option.cost_type': costType,
                  'agent_option.duration': duration as number,
                },
              });
            }

            const key = getCostMapKey(costType, duration as number);
            newCostMap.set(key, {
              costCredits: result.data?.cost_credits,
              originalCredits: result.data?.original_credits,
            });
          } catch (err) {
            console.error(`Failed to fetch cost for ${costType} with duration ${duration}:`, err);
          }
        }),
      );

      // 并发执行所有请求
      await Promise.all(requests);

      setAgentCreditCostMap(newCostMap);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch credit costs'));
    } finally {
      setLoading(false);
    }
  }, []);

  const { run: throttledFetchCosts } = useThrottleFn(fetchCosts, { wait: COST_FETCH_INTERVAL });

  return {
    loading,
    error,
    throttledFetchCosts,
  };
};
